import { useEffect, useState } from 'react';
import 'jsoneditor-react/es/editor.min.css';
import ace from 'brace';
import 'brace/mode/json';
import 'brace/theme/monokai';
import 'brace/ext/searchbox';

import Stack from 'rsuite/Stack';
import Button from 'rsuite/Button';
import Message from 'rsuite/Message';
import Loader from 'rsuite/Loader';
import { useToaster } from 'rsuite/esm/toaster';

// @ts-ignore
import { JsonEditor } from 'jsoneditor-react';

import './PrepListSettings.css';
import { useProducts } from './hooks/useProducts';
import { useUpdateProducts } from './hooks/useUpdateProducts';

type Product = {
  id?: number | string;
  [key: string]: unknown;
};

type ProductsApiResponse = {
  products: Product[];
};

type ProductsEditorValue = {
  products: Omit<Product, 'id'>[];
};

type AppError = {
  message?: string;
};

const Products = (): JSX.Element => {
  const [json, setJson] = useState<ProductsEditorValue | null>(null);

  const {
    data,
    isLoading,
    error,
    refetch
  } = useProducts() as {
    data?: ProductsApiResponse;
    isLoading: boolean;
    error?: unknown;
    refetch: () => void;
  };

  const {
    mutate: updateProducts,
    isLoading: savingInProgress
  } = useUpdateProducts() as {
    mutate: (
      payload: ProductsEditorValue | null,
      options?: {
        onSuccess?: () => void;
        onError?: () => void;
      }
    ) => void;
    isLoading: boolean;
  };

  const toaster = useToaster();

  useEffect(() => {
    if (!data?.products || !Array.isArray(data.products)) {
      return;
    }

    const productsWithoutId: ProductsEditorValue = {
      products: data.products.map(({ id, ...productWithoutId }) => productWithoutId)
    };

    setJson(productsWithoutId);
  }, [data]);

  const handleEditorChange = (value: unknown): void => {
    if (
      value &&
      typeof value === 'object' &&
      'products' in value &&
      Array.isArray((value as ProductsEditorValue).products)
    ) {
      setJson(value as ProductsEditorValue);
      return;
    }

    setJson({ products: [] });
  };

  const handleSave = (): void => {
    if (!json) {
      toaster.push(
        <Message showIcon type="warning" closable>
          No data available to save.
        </Message>,
        { placement: 'topEnd', duration: 3000 }
      );
      return;
    }

    updateProducts(json, {
      onSuccess: () => {
        toaster.push(
          <Message showIcon type="success" closable>
            Products saved successfully
          </Message>,
          { placement: 'topEnd', duration: 3000 }
        );

        refetch();
      },
      onError: () => {
        toaster.push(
          <Message showIcon type="error" closable>
            Error while saving products. Please try again.
          </Message>,
          { placement: 'topEnd', duration: 3000 }
        );
      }
    });
  };

  if (isLoading) {
    return <Loader center />;
  }

  if (error) {
    const errorMessage =
      typeof error === 'object' && error !== null && 'message' in error
        ? (error as AppError).message
        : 'Unknown error';

    return (
      <div className="alert alert-danger" role="alert">
        Error loading products: {errorMessage}
      </div>
    );
  }

  return (
    <div>
      {json ? (
        <JsonEditor
          value={json}
          onChange={handleEditorChange}
          mode="code"
          ace={ace}
          theme="ace/theme/monokai"
          navigationBar={true}
          statusBar={true}
          allowedModes={['code', 'tree']}
        />
      ) : (
        <Loader center />
      )}

      <Stack style={{ marginTop: '8px', marginBottom: '8px' }} direction="row-reverse">
        <Button onClick={handleSave} appearance="primary" disabled={savingInProgress}>
          {savingInProgress && <Loader size="sm" style={{ marginRight: '8px' }} />}
          Save
        </Button>
      </Stack>
    </div>
  );
};

export default Products;